<template>
  <div class="mx-auto encounters-page">
    <modal
      tokenType="delete"
      coverBackground
      agreeTracked
      :show="showModal"
      :id="selected ? getReadableId(selected) : null"
      :details="selected ? getEncounterInformation(selected) : []"
      @cancel="cancelModal"
      @agree="agreeAction"
    ></modal>
    <modal
      singleButton
      tokenType="resumeUnavailable"
      coverBackground
      :show="showResumeUnavailableModal"
      @agree="showResumeUnavailableModal = false"
    ></modal>
    <div v-if="encounters.length === 0" class="px-4 text-center">
      <mrx-translation token="app.encounter.none.title"></mrx-translation>
    </div>
    <table v-else class="encounters-table">
      <tr>
        <th class="encounters-table__heading">
          <mrx-translation token="app.encounter.id.title"></mrx-translation>
        </th>
        <th class="encounters-table__heading encounters-table__heading--actions">
          <mrx-translation token="app.encounter.actions.title"></mrx-translation>
        </th>
      </tr>
      <tr v-for="encounter in encounters" :key="encounter.session.uuid">
        <td class="encounters-table__details">
          <div class="encounter-identifier">{{ getReadableId(encounter) }}</div>
          <div v-for="row in getEncounterInformation(encounter)" :key="row.label" class="encounter-info">
            <strong>{{ row.label }}:</strong> {{ row.value }}
          </div>
          <div class="encounter-badge">{{ getPausedSummary(encounter) }}</div>
        </td>
        <td class="encounters-table__actions">
          <v-btn
            class="encounter-resume-btn"
            color="var(--primary-blue)"
            depressed
            @click="resumeEncounter(encounter)"
          >
            <mrx-translation token="app.dialog.button.resume"></mrx-translation>
          </v-btn>
          <v-btn
            class="ms-2 encounter-delete-btn"
            color="var(--danger)"
            depressed
            min-width="44"
            @click="openModal(encounter)"
          >
            <v-icon>{{ icons.mdiTrashCanOutline }}</v-icon>
          </v-btn>
        </td>
      </tr>
    </table>
    <v-btn class="encounters-back-btn" depressed block @click="goHome">
      <v-icon class="me-1">{{ icons.mdiArrowLeft }}</v-icon>
      <mrx-translation token="app.encounter.back_home.title"></mrx-translation>
    </v-btn>
  </div>
</template>

<script>
  import ScrollArrow from '../layouts/ScrollArrow.vue'
  import Modal from '@/components/layouts/Modal'
  import scrollArrow from '@/mixins/scrollArrow'
  import icons from '@/mixins/icons'
  import ageRanges from '@/mixins/ageRanges'
  import { mapActions, mapState } from 'pinia'
  import { useSavedEncountersStore } from '@/stores/savedEncounters'
  import { usePartnerConfigStore } from '@/stores/partnerConfig'
  import { useTrackStore } from '@/stores/track'
  import { resolveAssessmentMode } from '@/features/text-mode/analytics/text-mode-context'

  export default {
    name: "Encounters",
    mixins: [ageRanges, icons, scrollArrow],
    components: { Modal, ScrollArrow },
    data() {
      return {
        showModal: false,
        showResumeUnavailableModal: false,
        selected: null,
      }
    },
    computed: {
      ...mapState(useSavedEncountersStore, { saved: 'saved' }),
      ...mapState(usePartnerConfigStore, ['encounterPauseResume']),
      ...mapState(useTrackStore, { encounterIsActive: 'active' }),
      encounters() {
        return [...this.saved].sort((a, b) => (b.pausedAt || '').localeCompare(a.pausedAt || ''))
      },
    },
    methods: {
      clearSelection() {
        this.showModal = false
        this.selected = null
      },
      openModal(encounter) {
        this.selected = encounter
        this.showModal = true
      },
      cancelModal() {
        this.clearSelection()
      },
      agreeAction() {
        this.deleteEncounter()
      },
      secondsPaused(encounter) {
        if (!encounter.pausedAt) return null
        return Math.max(0, Math.round((Date.now() - new Date(encounter.pausedAt).getTime()) / 1000))
      },
      deleteEncounter() {
        const encounter = this.selected
        this.$track(this.$analyticsEvents.ENCOUNTER_DISCARDED, {
          component: 'Encounters',
          source: 'paused_list',
          encounter_id: encounter.session.uuid,
          mode: resolveAssessmentMode(encounter.textModeContext),
          seconds_paused: this.secondsPaused(encounter),
        })
        this.delete(encounter.session.uuid)
        this.clearSelection()
      },
      async resumeEncounter(encounter) {
        if (this.encounterIsActive) {
          this.showResumeUnavailableModal = true
          return
        }
        const pausedRoute = await this.load(encounter.session.uuid)
        this.$track(this.$analyticsEvents.ENCOUNTER_RESUMED, {
          component: 'Encounters',
          encounter_id: encounter.session.uuid,
          seconds_paused: this.secondsPaused(encounter),
        })
        this.$router.push(pausedRoute || '/overall-assessment')
      },
      getReadableId(encounter) {
        // Determine which panel name to use for the readable ID
        let idString = this.$t('app.encounter.no_id.title')
        const patientId = encounter?.textPatient?.patient?.id
        if (patientId) {
          return patientId.toString()
        }
        try {
          if (this.encounterPauseResume) {
            const panel = encounter.track.current.panels.find(p => p.name === this.encounterPauseResume)
            if (panel && panel.responses && panel.responses[0] && panel.responses[0].value) {
              idString = panel.responses[0].value.toString()
            }
          } else {
        // Fallback: check visit_id, then patient_id
        const visitIdPanel = encounter.track.current.panels.find(p => p.name === 'visit_id')
        if (visitIdPanel && visitIdPanel.responses && visitIdPanel.responses[0] && visitIdPanel.responses[0].value) {
          idString = visitIdPanel.responses[0].value.toString()
        } else {
          const patientIdPanel = encounter.track.current.panels.find(p => p.name === 'patient_id')
          if (patientIdPanel && patientIdPanel.responses && patientIdPanel.responses[0] && patientIdPanel.responses[0].value) {
            idString = patientIdPanel.responses[0].value.toString()
          }
        }
          }
        } catch (e) { }
        return idString
      },

      getPatientAge(encounter) {
        const ageInDays = encounter?.textPatient?.patient?.ageInDays
        if (ageInDays != null) {
          return `${this.ageDisplayString(ageInDays)}`
        }
        try {
          const agePanel = encounter.track.current.panels.find(p => p.name === 'age')
          const ageValue = agePanel && agePanel.responses && agePanel.responses[0] ? agePanel.responses[0].value : null
          return `${this.ageDisplayString(ageValue)}`
        } catch (e) {
          console.error('Error getting patient age:', e)
          return ''
        }
      },
      getPatientGender(encounter) {
        const sex = encounter?.textPatient?.patient?.sex
        if (sex) {
          return sex
        }
        try {
          const genderPanel = encounter.track.current.panels.find(p => p.name === 'gender')

          if (genderPanel && genderPanel.computation && genderPanel.computation == 'assignFemaleGender') {
            return 'female'
          }

          if (genderPanel && genderPanel.responses && genderPanel.responses.length > 0) {
            const selectedResponse = genderPanel.responses.find(r => r.selected)
            return selectedResponse && selectedResponse.value ? selectedResponse.value : ''
          }
          return ''
        } catch (e) {
          return ''
        }
      },
      getModeLabel(encounter) {
        const token = encounter?.guidedMode?.activeAssessmentPath
          ? 'app.assessment_mode.selection.guided.title'
          : 'app.assessment_mode.selection.text.title'
        return this.$t(token)
      },
      formatPausedAt(pausedAt) {
        if (!pausedAt) return ''
        const pausedDate = new Date(pausedAt)
        if (Number.isNaN(pausedDate.getTime())) return ''

        return new Intl.DateTimeFormat(this.$i18n.locale, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        }).format(pausedDate)
      },
      getPausedSummary(encounter) {
        const paused = `${this.getModeLabel(encounter)} · ${this.$t('app.encounter.paused.title')}`
        const pausedAt = this.formatPausedAt(encounter.pausedAt)

        return pausedAt ? `${paused} ${pausedAt}` : paused
      },
      goHome() {
        this.$router.push({ name: 'Start' })
      },
      getEncounterInformation(encounter) {
        const info = [];
        const age = this.getPatientAge(encounter);
        if (age !== '') {
          info.push({ label: this.$t('questions.age.title'), value: age });
        }
        const genderValue = this.getPatientGender(encounter);
        if (genderValue !== '') {
          info.push({ label: this.$t('questions.gender.title'), value: this.$t(`responses.gender.${genderValue}.title`) });
        }

        return info;
      },
      ...mapActions(useSavedEncountersStore, ['load', 'delete'])
    },
  }
</script>

<style scoped>
  .encounters-page {
    width: 100%;
  }

  @media screen and (min-width: 750px) {
    .encounters-page {
      width: 700px;
    }
  }

  .encounters-table {
    border-collapse: collapse;
    border-radius: 8px;
    margin: 0 auto;
    overflow: hidden;
    table-layout: fixed;
    width: 100%;
  }

  .encounters-table__heading {
    background-color: var(--deep-blue);
    color: white;
    font-size: 1rem;
    font-weight: 700;
    padding: 14px 16px;
    text-align: start;
  }

  .encounters-table__heading--actions {
    text-align: center;
    width: 38%;
  }

  .encounters-table__details,
  .encounters-table__actions {
    background-color: var(--primary-blue-30);
    padding: 16px;
    vertical-align: middle;
  }

  .encounters-table__actions {
    border-inline-start: 1px solid var(--primary-blue-30);
    text-align: center;
    white-space: nowrap;
  }

  .encounters-table tr + tr .encounters-table__details,
  .encounters-table tr + tr .encounters-table__actions {
    border-top: 1px solid var(--primary-blue-30);
  }

  .encounter-identifier {
    color: var(--deep-blue);
    font-size: 1.05rem;
    margin-bottom: 4px;
  }

  .encounter-info {
    color: var(--deep-blue);
    font-size: 0.95rem;
  }

  .encounter-badge {
    background-color: var(--primary-blue-10);
    border-radius: 999px;
    color: var(--primary-blue);
    display: inline-block;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    margin-top: 10px;
    padding: 4px 10px;
    text-transform: uppercase;
  }

  .encounter-resume-btn {
    color: white;
    font-size: 14px;
    font-weight: 600;
  }

  .encounter-delete-btn {
    color: white;
    padding: 0 12px;
  }

  .encounters-back-btn {
    background-color: var(--white-70) !important;
    color: var(--deep-blue);
    font-weight: 600;
    margin-top: 16px;
    text-transform: none;
  }
</style>
